import React, { Component, Suspense, lazy } from 'react';
import Inherit from '../../core/inherit';
import withHooks from '../../core/withHooks';
import ResetPasswordController from './reset-password.controller';
import ResetPasswordSuccess from './reset-password-success';
import { LockOutlined } from '@ant-design/icons';
import styles from './styles/reset-password.module.scss';
import SplitLayout from '../common/layouts/splitLayout/SplitLayout';
import { SplitLayoutSide } from '../common/layouts/splitLayout/splitLayout.constants';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import SigninRedirect from '../common/signInRedirect/SignInRedirect';
import { Button, ButtonSizes, Input, InputSizes, Text } from '@clockout-dev/design-elements';
import AuthMarketingSideFallback from '../common/authMarketingSideFallback/AuthMarketingSideFallback';

const ClockoutExampleSection = lazy(() =>
    import('../common/clockoutExampleSection/ClockoutExampleSection')
);

/**
 * @extends ClockoutController
 * @extends ResetPasswordController
 * @extends Component
 */
class ResetPassword extends Inherit(ResetPasswordController, Component) {
    state = {
        password: '',
        confirmPassword: '',
        loading: false,
        error: false,
        invitationValidating: false,
        invitationValid: null,
        invitationData: null,
        showSuccessScreen: false
    };

    render() {
        const { 
            password, 
            confirmPassword, 
            error, 
            loading, 
            invitationValidating,
            invitationValid,
            invitationData,
            showSuccessScreen
        } = this.state;

        // Show success screen if password reset was successful
        if (showSuccessScreen) {
            return <ResetPasswordSuccess />;
        }

        // Show loading while validating invitation
        if(invitationValidating) {
            return (
                <SplitLayout
                    sideVariant={SplitLayoutSide.LEFT}
                    side={
                        <Suspense fallback={<AuthMarketingSideFallback />}>
                            <ClockoutExampleSection
                                titleFirstPart="Setting up your password"
                                description="Please wait while we validate your reset password link and prepare your account."
                            />
                        </Suspense>
                    }
                    contentClassName={styles.resetPasswordContentContainer}
                    sideClassName={styles.resetPasswordSideColumn}
                >
                    <React.Fragment>
                        <SigninRedirect loading={loading} navigateLogin={this.navigateLogin} />

                        <div className={styles.resetCard}>
                            <div className={styles.loadingContent}>
                                <Text as="h1" size="display-m"  className={styles.formTitle}>Validating Reset Link...</Text>
                                <Text as="p" size="l" className={styles.loadingText}>Please wait while we validate your reset password link.</Text>
                            </div>
                        </div>
                    </React.Fragment>
                </SplitLayout>
            );
        }

        // Show invalid invitation component if invitation is invalid
        if (invitationValid === false) {
            return (
                <SplitLayout
                    sideVariant={SplitLayoutSide.LEFT}
                    side={
                        <Suspense fallback={<AuthMarketingSideFallback />}>
                            <ClockoutExampleSection
                                titleFirstPart="Invalid Link"
                                description="We encountered an issue with your link. Please see details on the right."
                            />
                        </Suspense>
                    }
                    contentClassName={styles.resetPasswordContentContainer}
                    sideClassName={styles.resetPasswordSideColumn}
                >
                    <SigninRedirect loading={loading} navigateLogin={this.navigateLogin} />

                    <div className={styles.resetCard}>
                        <div className={styles.loadingContent}>
                            <ExclamationCircleOutlined style={{ fontSize: 64, color: '#f59e0b', marginBottom: 24 }} />
                            <Text as='h1' size='display-m' className={styles.formTitle}>Invalid Link</Text>
                            <Text as="p" size="l" className={styles.loadingText}>
                                This link is invalid or has expired. Please contact your administrator for a new link to access your account.
                            </Text>
                            <Button
                                type="button"
                                size={ButtonSizes.MEDIUM}
                                onClick={() => this.navigateLogin()}
                                className={styles.continueButton}
                                block
                            >
                                Go to Login
                            </Button>
                        </div>
                    </div>
                </SplitLayout>
            );
        }

        // Show reset password form if invitation is valid
        if (invitationValid === true) {
            return (
                <SplitLayout
                    sideVariant={SplitLayoutSide.LEFT}
                    side={
                        <Suspense fallback={<AuthMarketingSideFallback />}>
                            <ClockoutExampleSection
                                titleFirstPart="Welcome to Clockout"
                                description="Set up your password to complete your account setup and start using your Earned Wage Access (EWA) service."
                            />
                        </Suspense>
                    }
                    contentClassName={styles.resetPasswordContentContainer}
                    sideClassName={styles.resetPasswordSideColumn}
                >
                    <SigninRedirect loading={loading} navigateLogin={this.navigateLogin} />

                    <div className={styles.resetCard}>
                        <Text as="h1" size="display-m" className={styles.formTitle}>Set Your Password</Text>
                        <Text as="p" size="l" className={styles.invitationEmail}>
                            Set up a new password for <strong>{invitationData?.email || 'your account'}</strong>
                        </Text>

                        <Input.Password
                            size={InputSizes.LARGE}
                            id="newPassword"
                            label="New Password"
                            prefix={<LockOutlined className={styles.inputIcon} />}
                            value={password || ''}
                            onChange={(e) => this.setPassword(e.target.value)}
                            placeholder="password"
                            status={error ? 'error' : ''}
                            className={styles.input}
                            classNames={{label: styles.inputLabel}}
                            autoComplete="new-password"
                        />

                        <Input.Password
                            size={InputSizes.LARGE}
                            id="confirmPassword"
                            label="Confirm Password"
                            value={confirmPassword || ''}
                            onChange={(e) => this.setConfirmPassword(e.target.value)}
                            placeholder="password"
                            status={error ? 'error' : ''}
                            className={styles.input}
                            classNames={{label: styles.inputLabel}}
                            autoComplete="new-password"
                        />

                        {error && (
                            <Text as="p" className={styles.errorMessage}>
                                Please ensure passwords match and are at least 8 characters long.
                            </Text>
                        )}

                        <Button
                            type="button"
                            size={ButtonSizes.MEDIUM}
                            loading={loading}
                            onClick={() => this.resetPassword()}
                            className={styles.continueButton}
                            block
                        >
                            Complete Setup
                        </Button>
                    </div>
                </SplitLayout>
            );
        }

        // Show loading if still initializing
        return (
            <SplitLayout
                sideVariant={SplitLayoutSide.LEFT}
                side={
                    <Suspense fallback={<AuthMarketingSideFallback />}>
                        <ClockoutExampleSection
                            titleFirstPart="Reset Password"
                            description="Choose a new password for your account."
                        />
                    </Suspense>
                }
                contentClassName={styles.resetPasswordContentContainer}
                sideClassName={styles.resetPasswordSideColumn}
            >
                <React.Fragment>
                    <SigninRedirect loading={loading} navigateLogin={this.navigateLogin} />

                    <div className={styles.resetCard}>
                        <div className={styles.loadingContent}>
                            <Text as="h1" size="display-m" className={styles.formTitle}>Loading...</Text>
                            <Text as="p" size="l" className={styles.loadingText}>Please wait while we load the reset password form.</Text>
                        </div>
                    </div>
                </React.Fragment>
            </SplitLayout>
        );
    }
}

export default withHooks(ResetPassword);

